import { Flex, ThemeIcon, Text, SegmentedControl, Box, Paper, Divider, Indicator, Card, SemiCircleProgress, Badge, LoadingOverlay, Progress } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { IconBarbellFilled, IconBike, IconCalendar, IconCalendarEvent, IconCalendarFilled, IconCalendarMonth, IconCalendarStats, IconCalendarWeek, IconClockHour7, IconFlame, IconSparkles, IconStopwatch, IconWeight } from '@tabler/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatItem } from '../components/StatItem';
import colors from '../utils/colors';
import { AttendanceBadge } from '../components/AttendanceBadge';
import { ShareToMediaCard, CarouselCards } from '../components/CarouselCards';
import { useDashboard } from '../hooks/useDashboard';
import { useLoadingOverlay } from '../components/LoadingOverlayContext';
import { useProfile } from '../hooks/useProfile';
import { useRanking } from '../hooks/useRanking';
import { InviteFriendCard } from '../components/InviteFriendCard';
import { useLanguage } from '../context/LanguageContext';
import { staggerContainer, fadeInFromTop, fadeInFromLeft, fadeInFromRight, fadeIn, heartbeat, ctaPulse, floatWithScale, heartbeatSmall, typingContainer, typingCharacter, fadeInFromBottom } from '../utils/animations';
import ScrollAnimatedSection from '../components/ScrollAnimatedSection';
import { configureDayjsLocale, getCurrentDayjsLocale } from '../utils/dateLocale';

type StatsType = "today" | "week" | "month" | "all";

const Dashboard: React.FC = () => {
  const { data, isLoading, error } = useDashboard();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: rankingData } = useRanking();
  const { setLoading } = useLoadingOverlay();
  const { translate, locale } = useLanguage();
  const [statsType, setStatsType] = useState<StatsType>("today");
  const [highlightedDates, setHighlightedDates] = useState<Date[]>([]);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [statChangeKey, setStatChangeKey] = useState(0);

  useEffect(() => {
    configureDayjsLocale(locale);
  }, [locale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(isLoading || profileLoading);
    
    if (!isLoading && !profileLoading && data) {
      setTimeout(() => setShouldAnimate(true), 50);
    }
  }, [isLoading, profileLoading, data, setLoading]);

  useEffect(() => {
    if (data?.calendar.dates) {
      const dates = data.calendar.dates.map(dateStr => new Date(dateStr));
      setHighlightedDates(dates);
    }
  }, [data]);

  const handleStatTypeChange = (value: string) => {
    setStatsType(value as StatsType);
    setStatChangeKey(prev => prev + 1);
  };

  const today = useMemo(() => new Date(), []);
  const oneWeekAgo = useMemo(() => new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), [today]);
  const oneMonthAgo = useMemo(() => new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), [today]);
  const threeMonthsAgo = useMemo(() => new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000), [today]);
  const sixMonthsAgo = useMemo(() => new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000), [today]);

  const [highlightedCount, setHighlightedCount] = useState({
    week: 0,
    month: 0,
    threeMonths: 0,
    sixMonths: 0
  });

  useEffect(() => {
    const countDatesInRange = (startDate: Date, endDate: Date) => {
      return highlightedDates.filter(
        (highlightedDate) =>
          highlightedDate >= startDate && highlightedDate <= endDate
      ).length;
    };
  
    if (highlightedDates.length > 0) {
      setHighlightedCount({
        week: countDatesInRange(oneWeekAgo, today),
        month: countDatesInRange(oneMonthAgo, today),
        threeMonths: countDatesInRange(threeMonthsAgo, today),
        sixMonths: countDatesInRange(sixMonthsAgo, today),
      });
    }
  }, [highlightedDates, oneWeekAgo, today, oneMonthAgo, threeMonthsAgo, sixMonthsAgo]);

  const stats = data?.stats[statsType];
  let studioActiveMeter = data?.studio.load ?? 0
  const studioActiveMeterRedIndicator = 90;
  const studioActiveMeterOrangeIndicator = 70;

  const level = rankingData?.level ?? 0;
  const exp = rankingData?.exp ?? 0;
  const expToLevelUp = rankingData?.expToLevelUp ?? 0;
  const scoreTotal = rankingData?.scoreTotal ?? 0;
  const currentLevelMin = rankingData?.currentLevelMin ?? 0;
  const nextLevelMin = rankingData?.nextLevelMin ?? 1000;

  const getExerciseFeedback = () => {
    if (highlightedCount.month <= 6) {
      return translate('exerciseMoreOften');
    } else if (highlightedCount.month > 10) {
      return translate('exerciseTooMuch');
    } else if (highlightedCount.month > 6) {
      return translate('exerciseRegular');
    }
    return '';
  };

  return (
    <motion.div 
      className="p-4 mt-6"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      exit={{ opacity: 0, transition: { duration: 0.3 } }}
      style={{ willChange: 'transform' }}
      layoutRoot
    >
      <motion.div 
        className="text-3xl font-semibold mt-1 mb-1 text-center"
        variants={fadeInFromTop}
        layout
      >
        <motion.div
          className="rainbow-text"
          variants={typingContainer}
          initial="hidden"
          animate="visible"
          style={{
            background: 'linear-gradient(90deg, #866b4c, #665d2c, #a18c11, #866b4c, #a18c11, #665d2c, #866b4c, #a18c11)',
            backgroundSize: '200% 100%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            animation: 'rainbow-flow 8s linear infinite',
            display: 'inline-block'
          }}
        >
          {translate('hello', {name: profile?.name || ''}).split('').map((char, index) => (
            <motion.span key={`char-${index}`} variants={typingCharacter}>
              {char}
            </motion.span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div variants={fadeInFromTop} layout>
        <Flex direction="column" gap={'md'} mt={'md'}>
          <Flex direction={'row'} justify={'center'} align={'center'}>
            <SegmentedControl
              radius="lg"
              size='xs'
              color={`${colors.custom36p}dd`}
              value={statsType}
              onChange={handleStatTypeChange}
              data={[
                { label: (<Flex gap={3} justify={'center'} align={'center'} fz={'14'}> <IconCalendarEvent size={16}/> <span>{translate('today')}</span> </Flex>), value: "today" },
                { label: (<Flex gap={3} justify={'center'} align={'center'} fz={'14'}> <IconCalendarWeek size={16}/> <span>{translate('week')}</span> </Flex>), value: "week" },
                { label: (<Flex gap={3} justify={'center'} align={'center'} fz={'14'}> <IconCalendarMonth size={16}/> <span>{translate('month')}</span> </Flex>), value: "month" },
                { label: (<Flex gap={3} justify={'center'} align={'center'} fz={'14'}> <IconCalendarStats size={16}/> <span>{translate('all')}</span> </Flex>), value: "all" },
              ]}
              w={350}
            />
          </Flex>
          
          <motion.div variants={fadeInFromTop} layout>
          <AnimatePresence mode="wait">
            <motion.div 
              key={statChangeKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.14 }}
            >
              <Flex style={{ flexFlow: 'row wrap'}} justify={'space-around'}>
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="overflow-x-hidden">
                  <Flex direction="column" gap={15}>
                    {!data?.studio.isStudioBasic && (
                      <motion.div variants={fadeInFromLeft }>
                        <StatItem icon={<IconBike />} value={stats ? String(stats.km) : '0'} unit='km' description={translate('distance')} gradient={{ from: 'grape', to: 'indigo', deg: 90 }} />
                      </motion.div>
                    )}
                    <motion.div variants={fadeInFromLeft}>
                      <StatItem icon={<IconWeight />} value={stats ? String(stats.kg) : '0'} unit='kg' description={translate('weight')} gradient={{ from: 'red', to: 'rgba(150, 3, 67, 1)', deg: 90 }} />
                    </motion.div>
                    {!data?.studio.isStudioBasic && (
                      <motion.div variants={fadeInFromLeft}>
                        <StatItem icon={<IconStopwatch />} value={stats ? String(stats.time) : '0'} unit={translate('minutes')} description={translate('duration')} gradient={{ from: 'teal', to: 'indigo', deg: 90 }} />
                      </motion.div>
                    )}
                  </Flex>
                  </div>
                </motion.div>
                <motion.div 
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  <div className="overflow-x-hidden">
                  <Flex direction="column" gap={15}>
                    {!data?.studio.isStudioBasic && (
                      <motion.div variants={fadeInFromRight}>
                        <StatItem icon={<IconFlame />} value={stats ? String(stats.kcal) : '0'} unit='kcal' description={translate('burnedCalories')} gradient={{ from: 'yellow', to: 'orange', deg: 90 }} />
                      </motion.div>
                    )}
                    <motion.div variants={fadeInFromRight}>
                      <StatItem icon={<IconBarbellFilled />} value={stats ? String(stats.reps) : '0'} unit={translate('reps')} description={translate('repetitions')} gradient={{ from: '#248b58', to: '#07aa51', deg: 90 }} />
                    </motion.div>
                    {/* tymczasowo wyłączony */}
                    {/* <motion.div variants={fadeInFromRight}>
                      <StatItem icon={<IconSparkles />} value={stats ? String(stats.exp) : '0'} unit='exp' description={translate('experience')} gradient={{ from: '#FFA992', to: '#FD0D92', deg: 90 }} />
                    </motion.div> */}
                  </Flex>
                  </div>
                </motion.div>
              </Flex>
            </motion.div>
          </AnimatePresence>
          </motion.div>
        </Flex>
      </motion.div>

      <motion.div variants={fadeInFromTop} layout>
        <Divider size="xs" mt={'lg'} mb={'md'} color={colors.custom36p} />
        
        <Flex justify="space-between" align="center" pos="relative">
          <Text c={colors.custom36s} fw={700} fz="12">{`${translate('rankLevel')} ${level}`}</Text>
          <Flex align="center" gap={4} pos="absolute" left="50%" mb={8} style={{ transform: 'translateX(-50%)' }}>
            <IconSparkles size={18} color={colors.custom36p} />
            <Text c={colors.custom36p} fw={700} fz="16">
              {`${scoreTotal} pkt`}
            </Text>
          </Flex>
          <Text c={colors.custom36s} fw={700} fz="12">{`${translate('rankLevel')} ${level + 1}`}</Text>
        </Flex>
          
        <Progress.Root styles={{ root: { backgroundColor: colors.customGray } }} size="xl">
          <Progress.Section color={colors.custom36p} value={(exp/(nextLevelMin - currentLevelMin))*100} animated>
          </Progress.Section>
        </Progress.Root>

        <Flex justify="space-between">
          <Text c={colors.custom36p} fw={700} fz="11">{`${currentLevelMin} pkt`}</Text>
          <motion.div 
            animate="animate" 
            variants={heartbeat}
            className="text-center"
            >
          <Text c={colors.custom36p} fw={700} fz="12" mt={3}>{translate('xpNeeded', { amount: expToLevelUp })}</Text>
          </motion.div>
          <Text c={colors.custom36p} fw={700} fz="11">{`${nextLevelMin} pkt`}</Text>
        </Flex>
      </motion.div>
        
      <motion.div variants={fadeInFromTop} layout className="overflow-x-hidden">
        <Divider size="xs" mt={'lg'} mb={'md'} color={colors.custom36p} />
        <motion.div 
          animate="animate" 
          variants={heartbeat}
          className="text-center"
        >
          <Text c={colors.custom36s} fw={500} fz="h4" mb={'sm'}>
            {getExerciseFeedback()}
          </Text>
        </motion.div>
      </motion.div>
      
      <motion.div variants={fadeInFromTop} layout>
        <Flex style={{ flexFlow: 'row wrap'}} justify={'space-around'} >
          <AttendanceBadge title={translate('last')} period={translate('lastWeekD')} count={highlightedCount.week} />
          <AttendanceBadge title={translate('last')} period={translate('lastMonthD')} count={highlightedCount.month} />
          <AttendanceBadge title={translate('last2')} period={translate('last3MonthsD')} count={highlightedCount.threeMonths} />
          <AttendanceBadge title={translate('last2')} period={translate('last6MonthsD')} count={highlightedCount.sixMonths} />
        </Flex>
      </motion.div>
      
      <motion.div variants={fadeInFromBottom} layout>
        <Flex direction="column" justify={'space-between'} align={'center'} mt={'lg'}>
          <Paper c={colors.custom36s} bg={`${colors.customGray}aa`} p={'md'} radius={'md'} >
            <Calendar
              static
              size='md'
              fw={700}
              c={colors.custom36s}
              locale={getCurrentDayjsLocale(locale)}
              styles={{
                day: {
                  color: colors.custom36s
                },
                weekday: {
                  fontWeight: 500,
                  color: colors.custom36s
                },
              }}
              renderDay={(date) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const isHighlighted = highlightedDates.some((highlightedDate) =>
                  highlightedDate.toDateString() === date.toDateString()
                );
                return (
                  <Indicator
                    size={isToday && isHighlighted ? 8 : 0}
                    color={isToday && isHighlighted ? colors.green : undefined}
                    offset={-1}
                    zIndex={3}
                    style={
                      isToday || isHighlighted
                        ? {
                            backgroundColor: isToday ? colors.custom36p : colors.green,
                            color: colors.white,
                            fontWeight: 'bold',
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }
                        : {}
                    }
                  >
                    <div>{date.getDate()}</div>
                  </Indicator>
                );
              }}
            />
          </Paper>
        </Flex>
      </motion.div>
        
      <ScrollAnimatedSection variants={fadeIn} viewAmount={0.6} delay={0.5}>
        <Divider size="xs" mt={'lg'} mb={'lg'} color={colors.custom36p} />
        <Flex direction={'row'} justify={'space-between'} align={'center'}>
            <Text c={colors.custom36s} fw={700} fz="h4">
              {translate('yourStudio')}
            </Text>
            <Badge c={colors.white} size='md' fw={700} color={colors.custom36p}>
              {data?.studio.name || '36 MINUT'}
            </Badge>
        </Flex>
        <Text c={colors.custom36p} fw={700} fz="h5" mt={'lg'} mb={-15} ta={'center'}>
          {translate('studioOccupancy')}
        </Text>
      </ScrollAnimatedSection>
        
      <ScrollAnimatedSection variants={fadeIn} viewAmount={0.4} delay={0.5}>
        <Flex justify={'center'} align={'center'} direction='column' p={'sm'}>
            <SemiCircleProgress
                fillDirection="left-to-right"
                orientation="up"
                filledSegmentColor={studioActiveMeter > studioActiveMeterRedIndicator ? colors.red : studioActiveMeter >= studioActiveMeterOrangeIndicator ? colors.orange : colors.green}
                emptySegmentColor={'#a1a1a1dd'}
                thickness={20}
                size={250}
                labelPosition="center"
                fz={'h1'}
                fw={500}
                c={studioActiveMeter > studioActiveMeterRedIndicator ? colors.red : studioActiveMeter >= studioActiveMeterOrangeIndicator ? colors.orange : colors.green}
                value={studioActiveMeter}
                label={`${studioActiveMeter}%`}
                styles={{label: {marginLeft: 8}}}
            />
            <motion.div 
              animate="animate" 
              variants={heartbeat}
              className="text-center"
            >
              <Text c={studioActiveMeter > studioActiveMeterRedIndicator ? colors.red : studioActiveMeter >= studioActiveMeterOrangeIndicator ? colors.orange : colors.green} fw={500} fz="h3" mb="sm">
                {studioActiveMeter > studioActiveMeterRedIndicator 
                  ? translate('highTraffic') 
                  : studioActiveMeter >= studioActiveMeterOrangeIndicator 
                    ? translate('mediumTraffic') 
                    : translate('lowTraffic')}
              </Text>
            </motion.div>
        </Flex>
      </ScrollAnimatedSection>
      
      <Flex direction={'column'} gap={'sm'}>
        <div className="overflow-x-hidden">
          <ScrollAnimatedSection variants={fadeInFromRight} viewAmount={0.2} delay={0.7}>
              <Divider size="xs" mb={'sm'} color={colors.custom36p} />
              <InviteFriendCard/>
          </ScrollAnimatedSection>
        </div>

        <div className="overflow-x-hidden">
          <ScrollAnimatedSection variants={fadeInFromLeft} viewAmount={0.3} delay={0.7}>
            <ShareToMediaCard/>
          </ScrollAnimatedSection>
        </div>
      </Flex>

    </motion.div>
  );
};

export default Dashboard;
